import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { TitleText } from "../../../ui/Text";
import Header from "../components/Header";
import SearchInput from "../../../ui/SearchInput";
import Modal from "../../../ui/Modal";
import { Formik, Field } from "formik";
import { DropDown, DropDownOptionsProps } from "../../../ui/DropDown";
import Input from "../../../ui/Input";
import { StatusIcon } from "../../../assets/svg/StatusIcon";
import Form, { InputSection } from "../components/Form";
import {
  useRouteMatch,
  NavLink,
  useParams,
  useLocation,
} from "react-router-dom";
import { get } from "../../../utils/get";
import { postEndpoint, GetHook, putEndpoint } from "../../../api/api";
import { OBJECTIVE_URL, PERSPECTIVE_URL } from "../../../api/urls";
import Axios from "axios";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { EditDeleteFormatter } from "../../../ui/TableFormatter";
import { ObjectiveSchema } from "../../../utils/validationSchema/ScorecardConfigSchema/ObjectiveSchema";
import { is360Subject } from "../../../subjects/Is360Subject";

const ObjectivesComponent = () => {
  const [isModalOpen, setisModalOpen] = useState(false);
  const [createRes, createReq] = postEndpoint();
  const [updateRes, updateReq] = putEndpoint();
  const [objectives, setObjectives] = useState<any>([]);
  const [isEdit, setIsEdit] = useState(false);
  const [objectiveId, setObjectiveId] = useState();
  const [editData, setEditData] = useState([]);
  const { id } = useParams();

  const { data, error, pending, reload } = GetHook(
    `${OBJECTIVE_URL}?perspective_id=${id}`
  );
  const { data: rawPerspectives } = GetHook(`${PERSPECTIVE_URL}`);

  const rawPerspective = rawPerspectives.filter(
    (rawP) => get(rawP, "id") === id
  );
  const { url } = useRouteMatch();
  const formRef: any = useRef<any>(null);
  const location = useLocation();

  const isAddMode = !objectiveId;

  useEffect(() => {
    setObjectives(data);
    return () => {};
  }, [data, id]);

  useEffect(() => {
    const isPerspective360 = !!get(rawPerspective, "[0].is_360");
    is360Subject.next(isPerspective360);
    return () => {};
  }, [rawPerspective]);

  const handleAddObjective = (values) => {
    const payload = {
      name: get(values, "name"),
      notes: get(values, "notes"),
      perspective_id: id,
    };
    createReq(OBJECTIVE_URL, payload);
  };

  useEffect(() => {
    const { complete, pending, data, error } = createRes;
    if (error) toast.error(get(data, "message.name"));
    if (!pending && complete && !error) {
      toast.success("Objective configured");
      setObjectives([data.returned_resultset[0], ...objectives]);
      setisModalOpen(false);
    }

    return () => {};
  }, [createRes]);

  const setEdit = (objective) => {
    setIsEdit(true);
    setObjectiveId(objective?.id);
    setEditData(objective);
    setisModalOpen(true);
  };

  const closeModal = () => {
    setisModalOpen(false);
    setIsEdit(false);
    setObjectiveId(undefined);
    formRef.current.resetForm(initialValues);
  };

  useEffect(() => {
    if (!isAddMode) {
      const fields = ["name", "notes"];

      fields.forEach((field) =>
        formRef.current.setFieldValue(field, editData[field], false)
      );
    }
  }, [isAddMode]);

  const editObjective = (values) => {
    updateReq(`${OBJECTIVE_URL}/${objectiveId}`, values);
  };
  useEffect(() => {
    const { complete, pending, data, error } = updateRes;
    if (!error && complete && data) {
      toast.success("Objective updated");
      closeModal();
      reload(get(location, "key"));
    }
  }, [setisModalOpen, updateRes]);

  const handleDelete = (id) => {
    Swal.fire({
      title: "Are you sure?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.value) {
        if (id) {
          Axios.delete(`${OBJECTIVE_URL}/${id}`)
            .then((res) => {
              setObjectives(
                objectives.filter((objective: any) => objective?.id != id)
              );
              toast.success("Objective has been deleted");
              reload(get(location, "key"));
            })
            .catch((error) => toast.error(error.response.data.message));
        }
      }
    });
  };

  const renderAddObjective = () => {
    return (
      <Form>
        <InputSection>
          <Field
            name="name"
            placeholder="enter objective name"
            label="Objective name"
            component={Input}
            isBordered={true}
            width="96%"
          />
        </InputSection>
        <InputSection>
          <Field
            type="textarea"
            name="notes"
            placeholder="Description"
            label="Objective description"
            component={Input}
          />
        </InputSection>
      </Form>
    );
  };
  const initialValues = {
    name: "",
    notes: "",
  };
  return (
    <Formik
      innerRef={formRef}
      initialValues={initialValues}
      validationSchema={ObjectiveSchema}
      onSubmit={isAddMode ? handleAddObjective : editObjective}
    >
      {({ handleSubmit }) => (
        <ObjectiveContainer>
          <Header
            title="Objectives"
            isEdit={isEdit}
            handleDelete={handleDelete}
            setisModalOpen={setisModalOpen}
          />
          {isModalOpen && (
            <Modal
              title={isEdit ? "Edit Objective" : "Add new Objective"}
              handleSubmit={handleSubmit}
              closeModal={closeModal}
            >
              {renderAddObjective()}
            </Modal>
          )}
          <SearchInput />
          <Objectives>
            {objectives &&
              objectives.map((objective: any) => (
                <ObjectivesItem
                  key={objective.id}
                  to={`${url}/objective/${objective?.id}`}
                  onDoubleClick={() => setEdit(objective)}
                >
                  <TitleText style={{ fontSize: "12px" }}>
                    {objective.name}
                  </TitleText>
                  <span
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                    }}
                  >
                    <EditDeleteFormatter
                      showEdit={() => setEdit(objective)}
                      handleDelete={() => handleDelete(objective?.id)}
                    />
                  </span>
                </ObjectivesItem>
              ))}
          </Objectives>
        </ObjectiveContainer>
      )}
    </Formik>
  );
};

export default ObjectivesComponent;

const ObjectiveContainer = styled.div`
  display: grid;
  grid-template-rows: 5em 4.25em auto;
  border-right: 1px solid rgba(55, 81, 255, 0.1);
`;

const Objectives = styled.ul`
  display: grid;
  grid-template-rows: repeat(auto-fill, 4.3125em);
  padding: 0;
  margin: 0;
  list-style: none;
`;

const ObjectivesItem = styled(NavLink)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  align-items: center;
  padding: 1.5em 1.4em;
  text-decoration: none;
  &.active {
    background-color: #ebefff;
  }
`;
